import { GetRealEstateWithTenantsResponse } from "api/real-estate"
import { BranchCode } from "api/risk"
import { GenericLoading } from "components/ui/Loader"
import { RealEstateDetails } from "features/subscription-process/home-insurance/RealEstateDetails"
import { ContextCode, SurveyName, useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey"
import { ProfilingMode } from "features/subscription-process/profiling-process/slices/types/enums"
import { FC, useEffect, useState } from "react"
import { realEstateAPI } from "services/swagger-api/realEstateAPI"
import { useContractInfos, useContractInfosState } from "../ContractInfosContext"
import { ContractParcOverview } from "./ContractParcOverview"

interface ContractRealEstateOverviewProps {
  branchCode: BranchCode
}

export const ContractRealEstateOverview: FC<ContractRealEstateOverviewProps> = ({ branchCode }) => {
  const { contractId } = useContractInfosState()
  const { setIsParc } = useContractInfos()
  const [realEstateWithSurvey, setRealEstateWithSurvey] = useState<GetRealEstateWithTenantsResponse>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { mapToSurveyFormValue } = useGenericSurvey()

  const { initSurvey } = useGenericSurvey()

  useEffect(() => {
    initSurvey(ContextCode.RealEstate, SurveyName.RealEstate, ProfilingMode.AutoAdvance)
  }, [initSurvey]);

  useEffect(() => {
    const getRealEstateDetails = async () => {
      try {
        setIsLoading(true)
        const response = await realEstateAPI.getRealEstateByContractId({ contractId, includeQuestionnaire: true, codeBranche: branchCode })
        if (response) {
          setRealEstateWithSurvey(response)
          setIsParc(!!response.realEstatePark)
          if (response.surveyAnswers)
            mapToSurveyFormValue(response.surveyAnswers)
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails immobiliers:', error)
      } finally {
        setIsLoading(false)
      }
    }
    getRealEstateDetails()
  }, [contractId, branchCode, mapToSurveyFormValue, setIsParc])

  if (!realEstateWithSurvey || isLoading) {
    return <div className="relative flex justify-center items-center p-8">
      <GenericLoading isParentSized />
    </div>
  }

  // Risque parc : afficher le parc avec mise en évidence des biens couverts.
  if (realEstateWithSurvey.realEstatePark) {
    return (
      <ContractParcOverview
        parc={realEstateWithSurvey.realEstatePark}
        coveredRealEstateIds={realEstateWithSurvey.coveredRealEstateIds ?? []}
      />
    )
  }

  if (!realEstateWithSurvey.realEstate) {
    return <div className="relative flex justify-center items-center p-8 text-sm text-gray-500">
      Aucun bien immobilier associé à ce contrat.
    </div>
  }

  return <RealEstateDetails realEstate={realEstateWithSurvey.realEstate} tenants={realEstateWithSurvey.tenants} />
}
